import { useEffect, useState } from "preact/hooks";
import { api } from "../api";
import { CandidateList } from "../components/CandidateList";
import { BarcodeScanner } from "../components/BarcodeScanner";
import type { FavoriteItem, FoodCandidate, FoodInputMethod } from "@hh/shared";

type Mode = "describe" | "photo" | "search" | "favorites" | "barcode";

const MODES: { key: Mode; label: string }[] = [
  { key: "describe", label: "Describe" },
  { key: "search", label: "Search" },
  { key: "barcode", label: "Barcode" },
  { key: "photo", label: "Photo" },
  { key: "favorites", label: "Favorites" },
];

function favToCandidate(f: FavoriteItem): FoodCandidate {
  return {
    food_id: f.food_id,
    source: "manual",
    barcode: null,
    name: f.name,
    brand: f.brand,
    per100_kcal: f.per100_kcal,
    per100_protein: f.per100_protein,
    per100_carbs: f.per100_carbs,
    per100_fat: f.per100_fat,
    per100_fiber: f.per100_fiber,
    default_serving: f.default_serving,
    serving_unit: f.serving_unit,
    amount: f.default_serving ?? 100,
    unit: f.default_serving ? "serving" : f.serving_unit,
    meal: "snack",
    confidence: "high",
  };
}

function bytesToBase64(buf: ArrayBuffer): string {
  let bin = "";
  const bytes = new Uint8Array(buf);
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

// Downscale to <=1024px JPEG before upload (fast + within vision limits).
// Falls back to the raw file if the canvas path fails (e.g. odd formats).
async function imageToJpegBase64(file: File): Promise<string> {
  try {
    const bitmap = await createImageBitmap(file);
    const max = 1024;
    const scale = Math.min(1, max / Math.max(bitmap.width, bitmap.height));
    const w = Math.round(bitmap.width * scale);
    const h = Math.round(bitmap.height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    canvas.getContext("2d")!.drawImage(bitmap, 0, 0, w, h);
    const blob: Blob = await new Promise((res, rej) =>
      canvas.toBlob((b) => (b ? res(b) : rej(new Error("toBlob failed"))), "image/jpeg", 0.8),
    );
    return bytesToBase64(await blob.arrayBuffer());
  } catch {
    return bytesToBase64(await file.arrayBuffer());
  }
}

export function Log({ onLogged }: { onLogged: () => void }) {
  const [mode, setMode] = useState<Mode>("describe");
  const [candidates, setCandidates] = useState<FoodCandidate[] | null>(null);
  const [method, setMethod] = useState<FoodInputMethod>("text");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [toast, setToast] = useState("");

  // mode-local inputs
  const [text, setText] = useState("");
  const [query, setQuery] = useState("");
  const [code, setCode] = useState("");
  const [scanning, setScanning] = useState(false);
  const [favorites, setFavorites] = useState<FavoriteItem[]>([]);

  useEffect(() => {
    api.favorites().then((r) => setFavorites(r.favorites)).catch(() => {});
  }, [candidates]);

  function present(cands: FoodCandidate[], m: FoodInputMethod) {
    setErr("");
    if (!cands.length) { setErr("Nothing found — try rephrasing or another method."); return; }
    setMethod(m);
    setCandidates(cands);
  }

  async function run(fn: () => Promise<void>) {
    setBusy(true); setErr("");
    try { await fn(); } catch (e) { setErr(e instanceof Error ? e.message : "Something went wrong"); }
    finally { setBusy(false); }
  }

  const doParse = () => run(async () => present((await api.parse(text)).candidates, "text"));
  const doSearch = () => run(async () => present((await api.search(query)).candidates, "favorite"));
  const lookupBarcode = (c: string) => run(async () => present([(await api.barcode(c.trim())).candidate], "barcode"));
  const doBarcode = () => lookupBarcode(code);
  const doPhoto = (file: File) => run(async () => {
    const b64 = await imageToJpegBase64(file);
    present((await api.photo(b64, "image/jpeg")).candidates, "photo");
  });

  function saved() {
    setCandidates(null);
    setText(""); setQuery(""); setCode("");
    setToast("Logged ✓");
    setTimeout(() => setToast(""), 1600);
    onLogged();
  }

  if (candidates) {
    return (
      <div class="app-shell fade-in">
        <div class="screen-head"><div><div class="eyebrow">Confirm & adjust</div><h1>Review</h1></div></div>
        <CandidateList
          candidates={candidates}
          inputMethod={method}
          onSaved={saved}
          onClear={() => setCandidates(null)}
        />
      </div>
    );
  }

  return (
    <div class="app-shell fade-in">
      <div class="screen-head"><div><div class="eyebrow">Quick add</div><h1>Log</h1></div></div>

      <div class="mode-row">
        {MODES.map((m) => (
          <button key={m.key} class={mode === m.key ? "active" : ""} onClick={() => { setMode(m.key); setErr(""); }}>
            {m.label}
          </button>
        ))}
      </div>

      {mode === "describe" && (
        <div class="card">
          <h2>What did you eat or drink?</h2>
          <textarea
            placeholder="e.g. 300ml whole milk, 22g Lindt 90%, 2 eggs, grande oat latte"
            value={text} onInput={(e) => setText((e.target as HTMLTextAreaElement).value)}
          />
          <p class="muted" style={{ margin: "8px 2px 14px" }}>Tap the mic on your keyboard to dictate.</p>
          <button class="btn" onClick={doParse} disabled={busy || text.trim().length < 2}>
            {busy ? <span class="spinner" /> : "Parse with AI"}
          </button>
        </div>
      )}

      {mode === "search" && (
        <div class="card">
          <h2>Search foods</h2>
          <div class="field" style={{ marginBottom: 12 }}>
            <input placeholder="e.g. banana, chicken breast"
              value={query} onInput={(e) => setQuery((e.target as HTMLInputElement).value)}
              onKeyDown={(e) => e.key === "Enter" && query.trim().length > 1 && doSearch()} />
          </div>
          <button class="btn" onClick={doSearch} disabled={busy || query.trim().length < 2}>
            {busy ? <span class="spinner" /> : "Search"}
          </button>
        </div>
      )}

      {mode === "barcode" && (
        <div class="card">
          <h2>Barcode</h2>
          {scanning ? (
            <BarcodeScanner
              onDetected={(c) => { setScanning(false); setCode(c); lookupBarcode(c); }}
              onClose={() => setScanning(false)}
            />
          ) : (
            <>
              <button class="btn" onClick={() => setScanning(true)} disabled={busy}>
                {busy ? <span class="spinner" /> : "Scan with camera"}
              </button>
              <div class="field" style={{ margin: "14px 0 12px" }}>
                <label>or enter the number</label>
                <input inputMode="numeric" placeholder="e.g. 3017620422003"
                  value={code} onInput={(e) => setCode((e.target as HTMLInputElement).value)} />
              </div>
              <button class="btn secondary" onClick={doBarcode} disabled={busy || code.trim().length < 6}>
                Look up
              </button>
            </>
          )}
        </div>
      )}

      {mode === "photo" && (
        <div class="card">
          <h2>Photo of your plate</h2>
          <label class="btn secondary" style={{ marginTop: 4 }}>
            {busy ? <span class="spinner" /> : "Take photo or choose from library"}
            <input type="file" accept="image/*" style={{ display: "none" }}
              onChange={(e) => {
                const f = (e.target as HTMLInputElement).files?.[0];
                if (f) doPhoto(f);
              }} />
          </label>
          <p class="muted" style={{ marginTop: 12 }}>Claude estimates the items and macros — you confirm before saving.</p>
        </div>
      )}

      {mode === "favorites" && (
        <div class="card">
          <h2>Recent & favorites</h2>
          {favorites.length === 0 ? (
            <p class="muted">Items you log will show up here for one-tap re-adding.</p>
          ) : (
            <div class="chips">
              {favorites.map((f) => (
                <button class="chip" key={f.food_id} onClick={() => present([favToCandidate(f)], "favorite")}>
                  {f.name}{f.brand && <span class="sub"> · {f.brand}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {err && <div class="err">{err}</div>}
      {toast && <div class="toast">{toast}</div>}
    </div>
  );
}
